type HasEntriesObjectType = Record<string, unknown> | unknown[];

/**
 * Determines whether the first object contains every entry in the second object.
 * @param object1 The object that may be a superset
 * @param object2 The object that may be contained within another object
 * @returns True if object1 contains all the entries from object2 or false otherwise
 */
export const hasEntries = (
  object1: HasEntriesObjectType,
  object2: HasEntriesObjectType
): boolean => {
  const anItemIsAnObject = typeof object1 === "object" || typeof object2 === "object";
  let isEqual = anItemIsAnObject && object1 === object2;

  if (!isEqual && anItemIsAnObject) {
    const object1Keys = Object.keys(object1);
    const object2Keys = Object.keys(object2);

    if (object1Keys.length >= object2Keys.length) {
      isEqual = object2Keys.every((key) => {
        const object1Value = getObjectValue(object1, key);
        const object2Value = getObjectValue(object2, key);
        let itemIsEqual: boolean;

        if (
          typeof object1Value === "object" &&
          object1Value !== null &&
          typeof object2Value === "object" &&
          object2Value !== null
        ) {
          itemIsEqual = hasEntries(
            getObjectValue(object1, key) as Record<string, unknown>,
            getObjectValue(object2, key) as Record<string, unknown>
          );
        } else {
          itemIsEqual = getObjectValue(object1, key) === getObjectValue(object2, key);
        }

        return itemIsEqual;
      });
    }
  }

  return isEqual;
};

const getObjectValue = (obj: HasEntriesObjectType, key: string) => {
  let value: unknown;

  if (Array.isArray(obj)) {
    const keyAsNumber = Number.parseInt(key, 10);

    if (/^\d+$/.test(key) && !Number.isNaN(keyAsNumber)) {
      value = obj[keyAsNumber];
    }
  } else {
    value = obj[key];
  }

  return value;
};
